import React, { useEffect, useState } from 'react';
import {
  getStripeConnectionStatus,
  saveStripeConnection,
  disconnectStripeConnection,
  stripeWebhookUrlForAccount,
} from '../shared/api/stripeConnection';

/**
 * Connect / disconnect Stripe BYOK for an account.
 * The Account named "System" is configured through this same panel and acts as
 * the fallback for other accounts.
 * @param {{ accountId?: string|null, title?: string, disabled?: boolean }} props
 */
function StripeConnectionPanel({
  accountId = null,
  title = 'Stripe',
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [status, setStatus] = useState(null);
  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('usd');

  const connectionAccountId = accountId != null ? String(accountId) : '';
  const webhookUrl = stripeWebhookUrlForAccount(connectionAccountId);

  const configured = !!(status?.usingOverride || status?.override?.configured);
  const hintSource = status?.override || {};
  const effectiveNote = status && !status.usingOverride && status.systemDefaultConfigured
    ? 'Using the System account Stripe connection until this account connects its own keys.'
    : null;

  async function refresh() {
    if (!accountId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getStripeConnectionStatus({ accountId });
      setStatus(data);
      const cur = data.override?.default_currency || data.effective?.default_currency || 'usd';
      setDefaultCurrency(cur);
    } catch (err) {
      setError(err.message || String(err));
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when target/account changes
  }, [accountId]);

  async function handleSave() {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const data = await saveStripeConnection({
        accountId,
        secretKey,
        publishableKey,
        webhookSecret,
        defaultCurrency,
      });
      setStatus(data);
      setSecretKey('');
      setPublishableKey('');
      setWebhookSecret('');
      setSuccess(configured ? 'Stripe connection updated.' : 'Stripe connected.');
      await refresh();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const data = await disconnectStripeConnection({
        accountId,
      });
      setStatus(data);
      setSecretKey('');
      setPublishableKey('');
      setWebhookSecret('');
      setSuccess('Account Stripe disconnected.');
      await refresh();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!accountId) {
    return null;
  }

  return (
    <div
      style={{
        gridColumn: '1 / span 2',
        border: '1px solid #dee2e6',
        borderRadius: 6,
        padding: 12,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>{title}</strong>
        <span style={{ fontSize: 13, color: configured ? '#198754' : '#6c757d' }}>
          {loading ? 'Loading…' : (configured ? 'Connected' : 'Not connected')}
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: '#495057' }}>
        {status?.isSystemAccount
          ? 'This System account Stripe connection is the default for accounts without their own connection.'
          : 'Paste this account’s Stripe API keys. If left unconfigured, payments use the System account Stripe connection.'}
      </p>
      {effectiveNote && (
        <p style={{ margin: 0, fontSize: 13, color: '#0d6efd' }}>{effectiveNote}</p>
      )}
      {configured && hintSource && (
        <p style={{ margin: 0, fontSize: 12, color: '#6c757d' }}>
          Secret: {hintSource.secret_key_hint || '—'}
          {' · '}
          Publishable: {hintSource.publishable_key_hint || '—'}
          {' · '}
          Webhook: {hintSource.webhook_secret_hint || '—'}
        </p>
      )}
      {webhookUrl && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="panel-label">Webhook endpoint URL</label>
          <input
            type="text"
            className="input input-full"
            readOnly
            value={webhookUrl}
            onFocus={(e) => e.target.select()}
            title="Create this endpoint in the Stripe Dashboard for this account"
          />
          <span style={{ fontSize: 12, color: '#6c757d' }}>
            In Stripe Dashboard → Developers → Webhooks, add this URL and events:
            {' '}checkout.session.completed, payment_intent.succeeded, payment_intent.payment_failed, charge.refunded.
            Paste the signing secret below.
          </span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px 12px' }}>
        <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="panel-label" htmlFor={`stripe-secret-${connectionAccountId}`}>Secret key</label>
          <input
            id={`stripe-secret-${connectionAccountId}`}
            type="password"
            autoComplete="off"
            className="input input-full"
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            disabled={disabled || busy}
            placeholder={configured ? 'Leave blank to keep existing' : 'sk_live_… or sk_test_…'}
          />
        </div>
        <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="panel-label" htmlFor={`stripe-pk-${connectionAccountId}`}>Publishable key</label>
          <input
            id={`stripe-pk-${connectionAccountId}`}
            type="password"
            autoComplete="off"
            className="input input-full"
            value={publishableKey}
            onChange={(e) => setPublishableKey(e.target.value)}
            disabled={disabled || busy}
            placeholder={configured ? 'Leave blank to keep existing' : 'pk_live_… or pk_test_…'}
          />
        </div>
        <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="panel-label" htmlFor={`stripe-whsec-${connectionAccountId}`}>Webhook signing secret</label>
          <input
            id={`stripe-whsec-${connectionAccountId}`}
            type="password"
            autoComplete="off"
            className="input input-full"
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            disabled={disabled || busy}
            placeholder={configured ? 'Leave blank to keep existing' : 'whsec_…'}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="panel-label" htmlFor={`stripe-currency-${connectionAccountId}`}>Default currency</label>
          <input
            id={`stripe-currency-${connectionAccountId}`}
            type="text"
            className="input input-full"
            value={defaultCurrency}
            onChange={(e) => setDefaultCurrency(e.target.value)}
            disabled={disabled || busy}
            placeholder="usd"
          />
        </div>
      </div>
      {error && <p style={{ margin: 0, fontSize: 13, color: '#dc3545' }}>{error}</p>}
      {success && <p style={{ margin: 0, fontSize: 13, color: '#198754' }}>{success}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={disabled || busy || loading}
        >
          {busy ? 'Saving…' : (configured ? 'Update Stripe' : 'Connect Stripe')}
        </button>
        {configured && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleDisconnect}
            disabled={disabled || busy || loading}
          >
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
}

export default StripeConnectionPanel;
